import { useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import "../NivTasksPage.css";

const FIELDS = [
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "type", label: "Type" },
];

const NEW_OPTION_COLOR = "#579bfc";

const generateOptionId = () =>
  `o_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/**
 * One editable option row (label + color + isDone toggle + delete).
 * Label/color are committed on blur to avoid a Firestore write per keystroke.
 */
function OptionRow({ field, option, onCommit, onToggleDone, onDelete }) {
  const [label, setLabel] = useState(option.label);
  const [color, setColor] = useState(option.color);

  const commit = () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setLabel(option.label);
      return;
    }
    if (trimmed !== option.label || color !== option.color) {
      onCommit(option.id, { label: trimmed, color });
    }
  };

  return (
    <div className="nivtasks-config-option">
      <input
        type="color"
        className="nivtasks-config-swatch"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        onBlur={commit}
        title="Option color"
      />
      <input
        className="nivtasks-config-label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      />
      {field === "status" && (
        <label className="nivtasks-config-done-toggle" title='Tasks with this status are hidden ("Done")'>
          <input
            type="checkbox"
            checked={option.isDone === true}
            onChange={() => onToggleDone(option.id)}
          />
          <span>Done</span>
        </label>
      )}
      <button
        type="button"
        className="nivtasks-icon-btn nivtasks-icon-btn-danger"
        onClick={() => onDelete(option)}
        aria-label={`Delete ${option.label}`}
      >
        <Trash size={15} />
      </button>
    </div>
  );
}

/**
 * Board config editor: manage Status / Priority / Type option sets.
 *
 * @param {Object}   config          - board config
 * @param {Array}    tasks           - all tasks (for delete-in-use counting)
 * @param {function} onSaveOptions   - (field, newOptions) => void
 * @param {function} onReassignTasks - async (field, taskIds, toId) => void
 * @param {function} showError       - toast
 */
function ConfigPanel({ config, tasks, onSaveOptions, onReassignTasks, showError }) {
  const [pendingDelete, setPendingDelete] = useState(null); // { field, option, usedBy, fallback }

  const getOptions = (field) => config?.options?.[field] || [];

  const handleCommit = (field, optionId, changes) => {
    onSaveOptions(
      field,
      getOptions(field).map((o) => (o.id === optionId ? { ...o, ...changes } : o))
    );
  };

  const handleToggleDone = (optionId) => {
    const options = getOptions("status");
    const target = options.find((o) => o.id === optionId);
    if (!target) return;
    const doneCount = options.filter((o) => o.isDone === true).length;
    if (target.isDone === true && doneCount <= 1) {
      showError("At least one status must be marked as Done");
      return;
    }
    handleCommit("status", optionId, { isDone: target.isDone !== true });
  };

  const handleAdd = (field) => {
    onSaveOptions(field, [
      ...getOptions(field),
      {
        id: generateOptionId(),
        label: "New option",
        color: NEW_OPTION_COLOR,
        ...(field === "status" ? { isDone: false } : {}),
      },
    ]);
  };

  const handleDeleteRequest = (field, option) => {
    const options = getOptions(field);
    if (field !== "type" && options.length <= 1) {
      showError("Cannot delete the last option");
      return;
    }
    if (field === "status" && option.isDone === true) {
      const doneCount = options.filter((o) => o.isDone === true).length;
      if (doneCount <= 1) {
        showError("Cannot delete the only Done status");
        return;
      }
    }
    const usedBy = tasks.filter((t) => t[field] === option.id).map((t) => t.id);
    const fallback = options.find((o) => o.id !== option.id) || null;
    if (usedBy.length === 0) {
      onSaveOptions(field, options.filter((o) => o.id !== option.id));
      return;
    }
    setPendingDelete({ field, option, usedBy, fallback });
  };

  const confirmDelete = async () => {
    const { field, option, usedBy, fallback } = pendingDelete;
    try {
      await onReassignTasks(field, usedBy, field === "type" ? fallback?.id ?? null : fallback.id);
      onSaveOptions(field, getOptions(field).filter((o) => o.id !== option.id));
    } catch {
      // reassign failed — error toast already shown, keep the option
    }
  };

  return (
    <div className="nivtasks-config-panel">
      {FIELDS.map(({ key, label }) => (
        <div key={key} className="nivtasks-config-column">
          <div className="nivtasks-config-column-header">{label}</div>
          {getOptions(key).map((option) => (
            <OptionRow
              key={option.id}
              field={key}
              option={option}
              onCommit={(optionId, changes) => handleCommit(key, optionId, changes)}
              onToggleDone={handleToggleDone}
              onDelete={(opt) => handleDeleteRequest(key, opt)}
            />
          ))}
          <button type="button" className="nivtasks-config-add" onClick={() => handleAdd(key)}>
            <Plus size={14} /> Add option
          </button>
        </div>
      ))}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete option in use"
        message={
          pendingDelete
            ? `"${pendingDelete.option.label}" is used by ${pendingDelete.usedBy.length} task(s). They will be reassigned to "${pendingDelete.fallback?.label || "None"}".`
            : ""
        }
        confirmText="Reassign & delete"
        cancelText="Cancel"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default ConfigPanel;
